import { Dynamic } from "solid-js/web";
import type { ComponentProps } from "solid-js";
import { cn } from "@bodega/shared";

/**
 * Bodega brand mark — a warm warehouse ("bodega/storehouse") with tidy shelves
 * under its roof, rendered in the signature amber→cloud-blue gradient: the warm
 * amber is your storehouse, the serene blue is your private cloud. Calm,
 * organised and premium; legible at any size and identical in light and dark
 * (brand stops are theme-independent). The gradient id is suffixed to stay
 * unique when several logos render on the same page (sidebar + auth).
 /** Waits for the app to finish booting (skeleton overlay disappears). */
let markCounter = 0;

function LogoMark(props: ComponentProps<"svg">) {
  const gradId = `bodega-mark-${(markCounter += 1)}`;
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id={gradId} x1="4" y1="3" x2="28" y2="29" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.79 0.13 72)" />
          <stop offset="1" stop-color="oklch(0.6 0.12 235)" />
        </linearGradient>
      </defs>
      {/* Rounded brand card (keeps the PWA/tile silhouette consistent) */}
      <rect x="4" y="3" width="24" height="26" rx="7" fill={`url(#${gradId})`} />
      {/* Warehouse: roof + walls + two shelf lines = an orderly storehouse */}
      <g
        stroke="white"
        stroke-width="2.1"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      >
        <path d="M9.3 15.4 16 10.2 22.7 15.4" />
        <path d="M10.7 15.4V23h10.6v-7.6" />
        <path d="M11 18.4h10" />
        <path d="M11 21h10" />
      </g>
    </svg>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  as?: "h1" | "p";
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 shrink-0 drop-shadow-sm" />
          <span class="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/70">
            Bodega
          </span>
        </>
      ) : (
        <>
          <LogoMark class="h-16 w-16 mb-2 shrink-0 drop-shadow-md transition-transform hover:scale-105 active:scale-95 duration-300" />
          <Dynamic
            component={wordmarkTag()}
            class="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/80"
          >
            Bodega
          </Dynamic>
        </>
      )}
    </div>
  );
}
